import { useRef, useState, useEffect, useCallback } from 'react';
import { RelatedProductCard } from './RelatedProductCard';
import type { ProductListing } from '../../types/products';

interface RelatedSectionProps {
  title: string;
  icon: string;
  products: ProductListing[];
  maxItems?: number;
  onAddToCart: (productId: number, formatId: number, quantity: number) => Promise<void>;
  type?: string;
  isEditMode?: boolean;
  onReorder?: (orderedIds: number[]) => void;
}

export function RelatedSection({ title, icon, products, maxItems = 4, onAddToCart, isEditMode, onReorder }: RelatedSectionProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    if (!trackRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = trackRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    updateArrows();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateArrows();
        ticking = false;
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  const scroll = (direction: 'left' | 'right') => {
    if (!trackRef.current) return;
    trackRef.current.scrollBy({
      left: direction === 'left' ? -trackRef.current.clientWidth * 0.8 : trackRef.current.clientWidth * 0.8,
      behavior: 'smooth',
    });
  };

  if (!Array.isArray(products) || products.length === 0) return null;

  if (isEditMode) {
    return (
      <div className="mb-8">
        <h3 className="mb-3 flex items-center gap-2 text-lg font-medium text-gray-700">
          <i className={`fa ${icon} text-gray-400`} />
          {title}
          <span className="text-xs font-normal text-gray-400">(max {maxItems})</span>
        </h3>
        <DraggableProductList
          products={products}
          maxItems={maxItems}
          onReorder={onReorder}
        />
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h3 className="mb-3 flex items-center gap-2 text-lg font-medium text-gray-700">
        <i className={`fa ${icon} text-gray-400`} />
        {title}
      </h3>
      <div className="relative">
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute -left-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition-colors hover:border-green-500 hover:text-green-600 sm:flex"
            aria-label="Anterior"
          >
            <i className="fa fa-chevron-left text-xs" />
          </button>
        )}

        <div
          ref={trackRef}
          className="flex gap-3 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {products.slice(0, maxItems).map(product => (
            <RelatedProductCard key={product.id} product={product} onAddToCart={onAddToCart} />
          ))}
        </div>

        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute -right-3 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition-colors hover:border-green-500 hover:text-green-600 sm:flex"
            aria-label="Siguiente"
          >
            <i className="fa fa-chevron-right text-xs" />
          </button>
        )}
      </div>
    </div>
  );
}

// --- Drag and drop reorder ---

function DraggableProductList({
  products,
  maxItems,
  onReorder,
}: {
  products: ProductListing[];
  maxItems: number;
  onReorder?: (orderedIds: number[]) => void;
}) {
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [overId, setOverId] = useState<number | null>(null);
  const draggedRef = useRef<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, id: number) => {
    draggedRef.current = id;
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox
    e.dataTransfer.setData('text/plain', String(id));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, id: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverId(id);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    const sourceId = draggedRef.current;
    if (sourceId == null || sourceId === targetId) {
      setDraggedId(null);
      setOverId(null);
      return;
    }

    const ids = products.map(p => p.id);
    const fromIdx = ids.indexOf(sourceId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;

    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, sourceId);

    setDraggedId(null);
    setOverId(null);
    onReorder?.(ids);
  }, [products, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setOverId(null);
  }, []);

  return (
    <div className="flex flex-wrap gap-2">
      {products.map((product, index) => {
        const isVisible = index < maxItems;
        const isDragged = draggedId === product.id;
        const isOver = overId === product.id;

        return (
          <div
            key={product.id}
            draggable
            onDragStart={(e) => handleDragStart(e, product.id)}
            onDragOver={(e) => handleDragOver(e, product.id)}
            onDrop={(e) => handleDrop(e, product.id)}
            onDragEnd={handleDragEnd}
            className={`flex cursor-grab items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-all active:cursor-grabbing ${
              isDragged
                ? 'border-green-500 bg-green-50 opacity-50'
                : isOver
                  ? 'border-green-500 bg-green-50 ring-2 ring-green-400'
                  : isVisible
                    ? 'border-gray-200 bg-white'
                    : 'border-gray-200 bg-gray-50 opacity-40'
            }`}
          >
            <i className="fa fa-grip-vertical text-xs text-gray-300" />
            {product.has_image && (
              <img
                src={product.webp_image_url}
                alt=""
                className="h-8 w-8 rounded object-contain"
              />
            )}
            <span className={isVisible ? 'text-gray-700' : 'text-gray-400'}>
              {product.title}
            </span>
            {!isVisible && (
              <span className="text-[10px] text-gray-400">(oculto)</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
